import React, { memo, useContext, useState, useMemo } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import { contextMenuSx } from './styles';

const ContextMenuContext = React.createContext(null);

const ContextMenu = ({ children }) => {
  // the position to anchor the context menu to
  const [anchorPosition, setAnchorPosition] = useState(null);
  // the element to anchor the context menu to
  const [anchorElement, setAnchorElement] = useState(null);
  // the menu items for the context menu
  const [menuItems, setMenuItems] = useState([]);
  // the handler to call when the context menu is closed
  const [onClose, setOnClose] = useState(null);

  // the anchor reference, depending on where the context menu is anchored to
  const anchorReference = useMemo(() => (anchorElement ? 'anchorEl' : 'anchorPosition'), [anchorElement]);

  /**
   * Shows the context menu with the specified items.
   *
   * @param event the mouse event from the calling mouse event handler
   * @param menuItems the menu items for the context menu
   * @param onClose the handler to call when the context menu is closed
   * @param anchorToElement specifies whether to anchor the context menu to the event's target element instead of
   *                        its mouse click position
   */
  const showContextMenu = (event, menuItems, onClose, anchorToElement) => {
    event.preventDefault();
    if (anchorToElement) {
      setAnchorElement(event.target);
      setAnchorPosition(null);
    } else {
      setAnchorPosition({ top: event.pageY, left: event.pageX });
      setAnchorElement(null);
    }
    setOnClose(() => onClose);
    setMenuItems(menuItems);
  };

  const hideContextMenu = () => {
    onClose?.();
    setAnchorElement(null);
    setAnchorPosition(null);
  };

  return (
    <ContextMenuContext.Provider value={{ showContextMenu }}>
      <>
        <Menu
          sx={contextMenuSx}
          keepMounted
          open={!!(anchorPosition || anchorElement)}
          onClose={hideContextMenu}
          anchorReference={anchorReference}
          anchorEl={anchorElement}
          anchorPosition={anchorPosition}
          disableRestoreFocus
          data-test="contextMenu"
        >
          {menuItems.map((item, index) => {
            const { icon: Icon, onClick, label, isDisabled } = item;
            return (
              <MenuItem
                dense
                onClick={event => {
                  // execute the menu item's click handler
                  onClick(event);
                  // hide the context menu
                  hideContextMenu();
                }}
                key={index}
                disabled={isDisabled}
                data-id={item.id}
              >
                <ListItem disablePadding>
                  <ListItemIcon>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  {label}
                </ListItem>
              </MenuItem>
            );
          })}
        </Menu>
        {children}
      </>
    </ContextMenuContext.Provider>
  );
};

export default memo(ContextMenu);

export const useContextMenu = () => {
  return useContext(ContextMenuContext);
};
